import { LetterStatus } from "./helpers"

// Enhanced game state interface for daily tracking
export interface IDailyGameState {
  date: string
  completed: boolean
  won: boolean
  attempts: number
  guesses: string[]
  currentWord: string
  board: string[][]
  boardStatus: LetterStatus[][]
  currentAttempt: number
  letterPos: number
  letterStatus: Map<string, LetterStatus>
  gameOver: boolean
}

// Legacy game state interface for migration
interface ILegacyGameState {
  board?: string[][]
  boardStatus?: LetterStatus[][]
  currAttempt?: { attempt: number; letterPos: number }
  correctWord?: string
  letterStatus?: Map<string, LetterStatus>
  gameOver?: { gameOver: boolean; guessedWord: boolean }
}

export class GameStateManager {
  private static readonly STORAGE_KEY_PREFIX = "wordle-afrikaans"
  private static readonly LEGACY_STORAGE_KEY = "wordle-game-state"
  private static storageAvailable: boolean | null = null
  private static errorNotificationCallback?: (message: string, isError: boolean) => void

  /**
   * Set callback for error notifications
   * @param callback - Function to call when errors occur
   */
  static setErrorNotificationCallback(callback: (message: string, isError: boolean) => void): void {
    this.errorNotificationCallback = callback
  }

  /**
   * Check if localStorage is available and working
   * @returns boolean - True if localStorage is available, false otherwise
   */
  private static isStorageAvailable(): boolean {
    if (this.storageAvailable !== null) {
      return this.storageAvailable
    }

    try {
      const testKey = '__storage_test__'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      this.storageAvailable = true
      return true
    } catch (error) {
      console.warn('localStorage is not available:', error)
      this.storageAvailable = false
      
      if (this.errorNotificationCallback) {
        this.errorNotificationCallback(
          'Game progress cannot be saved. Your progress will be lost when you refresh the page.',
          true
        )
      }
      
      return false
    }
  }

  /**
   * Get the current local date in YYYY-MM-DD format with fallback handling
   */
  private static getCurrentDateString(): string {
    try {
      // Primary method: use en-CA locale for consistent YYYY-MM-DD format
      const date = new Date().toLocaleDateString('en-CA')
      
      // Validate the format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new Error('Invalid date format from toLocaleDateString')
      }
      
      return date
    } catch (error) {
      console.warn('Error getting local date with en-CA locale, using fallback:', error)
      
      try {
        // Fallback 1: Manual formatting from local date
        const now = new Date()
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const fallbackDate = `${year}-${month}-${day}`
        
        // Using fallback local date
        return fallbackDate
      } catch (fallbackError) {
        console.warn('Local date fallback failed, using UTC:', fallbackError)
        
        // Fallback 2: UTC date as last resort
        const now = new Date()
        const year = now.getUTCFullYear()
        const month = String(now.getUTCMonth() + 1).padStart(2, '0')
        const day = String(now.getUTCDate()).padStart(2, '0')
        const utcDate = `${year}-${month}-${day}`
        
        if (this.errorNotificationCallback) {
          this.errorNotificationCallback(
            'Date calculation issue detected. Using UTC time as fallback.',
            true
          )
        }
        
        // Using UTC date as final fallback
        return utcDate
      }
    }
  }

  /**
   * Generate storage key for a specific date
   */
  private static getStorageKey(date: string): string {
    return `${this.STORAGE_KEY_PREFIX}-${date}`
  }

  /**
   * Load game state for a specific date
   */
  static loadGameState(date?: string): IDailyGameState | null {
    if (!this.isStorageAvailable()) {
      return null
    }

    const targetDate = date || this.getCurrentDateString()
    const storageKey = this.getStorageKey(targetDate)

    try {
      const savedState = localStorage.getItem(storageKey)
      if (!savedState) {
        return null
      }

      const parsedState = JSON.parse(savedState)
      
      // Validate the loaded state
      if (!this.isValidGameState(parsedState)) {
        console.warn(`Invalid game state found for date ${targetDate}, removing`)
        try {
          localStorage.removeItem(storageKey)
        } catch (removeError) {
          console.warn('Failed to remove invalid game state:', removeError)
        }
        return null
      }

      // Convert letterStatus back to Map if it exists
      if (parsedState.letterStatus && typeof parsedState.letterStatus === 'object') {
        parsedState.letterStatus = new Map(Object.entries(parsedState.letterStatus))
      }

      return parsedState as IDailyGameState
    } catch (error) {
      console.error(`Error loading game state for ${targetDate}:`, error)
      
      if (error instanceof DOMException) {
        if (this.errorNotificationCallback) {
          this.errorNotificationCallback(
            'Unable to load saved game progress. Starting fresh game.',
            true
          )
        }
      }
      
      return null
    }
  }

  /**
   * Save game state for the current date
   */
  static saveGameState(state: IDailyGameState): void {
    if (!this.isStorageAvailable()) {
      console.warn('Cannot save game state: localStorage not available')
      return
    }

    const storageKey = this.getStorageKey(state.date)

    try {
      // Convert Map to object for JSON serialization
      const stateToSave = {
        ...state,
        letterStatus: state.letterStatus ? Object.fromEntries(state.letterStatus) : {}
      }

      const serializedState = JSON.stringify(stateToSave)
      localStorage.setItem(storageKey, serializedState)
      
    } catch (error) {
      console.error(`Error saving game state for ${state.date}:`, error)
      
      // Handle different types of storage errors
      if (error instanceof DOMException) {
        if (error.code === 22 || error.name === 'QuotaExceededError') {
          // Storage quota exceeded
          // Storage quota exceeded, attempting cleanup
          
          if (this.errorNotificationCallback) {
            this.errorNotificationCallback(
              'Storage space full. Clearing old game data...',
              true
            )
          }
          
          this.clearOldGameStates()
          
          // Retry after cleanup
          try {
            const stateToSave = {
              ...state,
              letterStatus: state.letterStatus ? Object.fromEntries(state.letterStatus) : {}
            }
            localStorage.setItem(storageKey, JSON.stringify(stateToSave))
            
            if (this.errorNotificationCallback) {
              this.errorNotificationCallback(
                'Game progress saved successfully after cleanup.',
                false
              )
            }
          } catch (retryError) {
            console.error('Failed to save game state even after cleanup:', retryError)
            
            if (this.errorNotificationCallback) {
              this.errorNotificationCallback(
                'Unable to save game progress. Your progress may be lost.',
                true
              )
            }
          }
        } else {
          // Other DOM exceptions (security, etc.)
          if (this.errorNotificationCallback) {
            this.errorNotificationCallback(
              'Cannot save game progress due to browser restrictions.',
              true
            )
          }
        }
      } else {
        // Other errors (JSON serialization, etc.)
        if (this.errorNotificationCallback) {
          this.errorNotificationCallback(
            'Error saving game progress. Your progress may be lost.',
            true
          )
        }
      }
    }
  }

  /**
   * Check if a game is completed for a specific date
   */
  static isGameCompleted(date?: string): boolean {
    const gameState = this.loadGameState(date)
    return gameState?.completed || false
  }

  /**
   * Get the current game state or create a new one
   */
  static getCurrentGameState(currentWord: string): IDailyGameState {
    const currentDate = this.getCurrentDateString()
    const existingState = this.loadGameState(currentDate)

    if (existingState && existingState.currentWord === currentWord) {
      return existingState
    }

    // Create new game state for today
    return this.createNewGameState(currentDate, currentWord)
  }

  /**
   * Create a new game state for a specific date and word
   */
  static createNewGameState(date: string, word: string): IDailyGameState {
    return {
      date,
      completed: false,
      won: false,
      attempts: 0,
      guesses: [],
      currentWord: word,
      board: Array(6).fill(null).map(() => Array(5).fill("")),
      boardStatus: Array(6).fill(null).map(() => Array(5).fill(LetterStatus.Unknown)),
      currentAttempt: 0,
      letterPos: 0,
      letterStatus: new Map(),
      gameOver: false
    }
  }

  /**
   * Migrate legacy game state to new format
   */
  static migrateLegacyState(): void {
    try {
      const legacyState = localStorage.getItem(this.LEGACY_STORAGE_KEY)
      if (!legacyState) {
        return
      }

      const parsed: ILegacyGameState = JSON.parse(legacyState)
      const currentDate = this.getCurrentDateString()

      // Only migrate if there's meaningful game progress
      if (parsed.currAttempt && parsed.currAttempt.attempt > 0) {
        const migratedState: IDailyGameState = {
          date: currentDate,
          completed: parsed.gameOver?.gameOver || false,
          won: parsed.gameOver?.guessedWord || false,
          attempts: parsed.currAttempt.attempt,
          guesses: parsed.board?.slice(0, parsed.currAttempt.attempt).map(row => row.join("")) || [],
          currentWord: parsed.correctWord || "",
          board: parsed.board || Array(6).fill(null).map(() => Array(5).fill("")),
          boardStatus: parsed.boardStatus || Array(6).fill(null).map(() => Array(5).fill(LetterStatus.Unknown)),
          currentAttempt: parsed.currAttempt.attempt,
          letterPos: parsed.currAttempt.letterPos,
          letterStatus: parsed.letterStatus || new Map(),
          gameOver: parsed.gameOver?.gameOver || false
        }

        this.saveGameState(migratedState)
        // Successfully migrated legacy game state
      }

      // Remove legacy state after migration
      localStorage.removeItem(this.LEGACY_STORAGE_KEY)
    } catch (error) {
      console.error('Error migrating legacy game state:', error)
    }
  }

  /**
   * Validate game state structure
   */
  private static isValidGameState(state: any): boolean {
    return (
      state &&
      typeof state.date === 'string' &&
      typeof state.completed === 'boolean' &&
      typeof state.won === 'boolean' &&
      typeof state.attempts === 'number' &&
      Array.isArray(state.guesses) &&
      typeof state.currentWord === 'string' &&
      Array.isArray(state.board) &&
      Array.isArray(state.boardStatus) &&
      typeof state.currentAttempt === 'number' &&
      typeof state.letterPos === 'number' &&
      typeof state.gameOver === 'boolean'
    )
  }

  /**
   * Clear old game states to free up localStorage space
   */
  private static clearOldGameStates(): void {
    if (!this.isStorageAvailable()) {
      return
    }

    try {
      const currentDate = new Date()
      const thirtyDaysAgo = new Date(currentDate.getTime() - (30 * 24 * 60 * 60 * 1000))
      const keysToRemove: string[] = []

      // Collect keys to remove (avoid modifying localStorage while iterating)
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          const dateMatch = key.match(/wordle-afrikaans-(\d{4}-\d{2}-\d{2})/)
          if (dateMatch) {
            try {
              const stateDate = new Date(dateMatch[1])
              if (!isNaN(stateDate.getTime()) && stateDate < thirtyDaysAgo) {
                keysToRemove.push(key)
              }
            } catch (dateError) {
              // Invalid date in key, remove it
              console.warn(`Invalid date in storage key: ${key}`)
              keysToRemove.push(key)
            }
          }
        }
      }

      // Remove collected keys
      let removedCount = 0
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key)
          removedCount++
          // Cleared old game state
        } catch (removeError) {
          console.warn(`Failed to remove key ${key}:`, removeError)
        }
      })

      if (removedCount > 0) {
        // Cleared old game states
      }

      // If still having issues, try more aggressive cleanup
      if (keysToRemove.length === 0) {
        this.emergencyStorageCleanup()
      }

    } catch (error) {
      console.error('Error during storage cleanup:', error)
      
      // Try emergency cleanup as last resort
      this.emergencyStorageCleanup()
    }
  }

  /**
   * Emergency storage cleanup - removes all but the most recent game states
   */
  private static emergencyStorageCleanup(): void {
    try {
      // Performing emergency storage cleanup
      
      const gameStateKeys: { key: string; date: Date }[] = []
      
      // Collect all game state keys with their dates
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          const dateMatch = key.match(/wordle-afrikaans-(\d{4}-\d{2}-\d{2})/)
          if (dateMatch) {
            try {
              const date = new Date(dateMatch[1])
              if (!isNaN(date.getTime())) {
                gameStateKeys.push({ key, date })
              }
            } catch (dateError) {
              // Remove invalid date keys
              localStorage.removeItem(key)
            }
          }
        }
      }
      
      // Sort by date (newest first) and keep only the 7 most recent
      gameStateKeys.sort((a, b) => b.date.getTime() - a.date.getTime())
      const keysToKeep = gameStateKeys.slice(0, 7).map(item => item.key)
      const keysToRemove = gameStateKeys.slice(7).map(item => item.key)
      
      // Remove older keys
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key)
          // Emergency cleanup removed key
        } catch (removeError) {
          console.warn(`Failed to remove key during emergency cleanup: ${key}`)
        }
      })
      
      // Emergency cleanup completed
      
    } catch (error) {
      console.error('Emergency storage cleanup failed:', error)
    }
  }

  /**
   * Reset game state for a new day
   */
  static resetForNewDay(newWord: string): IDailyGameState {
    const currentDate = this.getCurrentDateString()
    return this.createNewGameState(currentDate, newWord)
  }
}